import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";

//Components
import {
    Container,
    Breadcrumb,
    Loading
} from "@gpstrackit/gtc-react-controls";

import { Tabs, TabContainer, IconButton, Button, DynamicTooltip } from "@gpstrackit/gtc-react-controls";
import { TagsGroup } from "../../../../components/Tag";
import { Grid, Paper, Skeleton } from '@mui/material';
import { Timezone } from 'Components/uiControls';
import Collapse from '@mui/material/Collapse';

//Containers
import Preferences from "Core/containers/Preferences";

//Actions
import { savePreference, resetOverviewTab, resetImprovementPlanTab, resetRiskTab, resetEditCoachPanel } from 'Redux/actions'
import { getCaseDetail, reset } from './redux/slice'
import { updateDriverPerformanceCase } from '../implovementPlanTab/redux/slice';

// Views
import DriverInfo from '../events/modals/driverInfo';

let OVERVIEW_KEY = 'casesDetail'

//Styles
import './index.scss'

//Views
import OverviewTab from "../overviewTab";
import ImplovementPlanTab from "../implovementPlanTab";
import RiskTab from "../riskTab";

const CustomerDetail = () => {

    const { messages } = useIntl()

    //Hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tabRef = useRef();

    // Params
    const [tab, setTab] = useState(0);
    const { id } = useParams();

    // Improvement Plan States
    const [notes, setNotes] = useState('');
    const [changeNotes, setChangeNotes] = useState(false);
    const [improvementOmit, setImprovementOmit] = useState(false);
    const [targetValues, setTargetValues] = useState({
        avgEvents: 0,
        riskScore: 0,
        totalEvents: 0,
        totalDistance: 0
    });

    // Redux States
    const updateDriverPerformanceCaseResult = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.updateDriverPerformanceCase);
    const loadingUpdateDriverPerformanceCase = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.loadingUpdateDriverPerformanceCase);
    const caseDetail = useSelector((state) => state.driverPerformanceCaseDetailRedux.caseDetail);
    const loadingCaseDetail = useSelector((state) => state.driverPerformanceCaseDetailRedux.loading);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const openModal = useSelector(state => state.driverPerformanceCasesTabEditCoachRedux.open);
    const resultUpdate = useSelector(state => state.driverPerformanceCasesTabEditCoachRedux.resultUpdate);
    const loadingUpdateCoach = useSelector((state) => state.driverPerformanceCasesTabEditCoachRedux.loadingUpdate);
    const loadingCreateComment = useSelector((state) => state.driverPerformanceRiskTabRedux.loadingCreateDeviceReadingExtraField);
    const loadingUpdateCaseEventFlags = useSelector((state) => state.driverPerformanceRiskTabRedux.loadingUpdateCaseEventFlags);

    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone, isKph } = user;

    const pendingStatus = ['pendingAssing', 'pendingInitiate']

    //Local States
    const [checked, setChecked] = useState(true);

    // Effects
    useEffect(() => {
        if (id) {
            dispatch(getCaseDetail({ caseId: id }))
        }
        return () => {
            dispatch(reset({ caseDetail: null }))
            dispatch(resetOverviewTab({
                totalCoachingHistory: 0,
                coachingHistory: [],
                statsSinceLastCoaching: [],
                riskHistory: []
            }))
            dispatch(resetImprovementPlanTab({
                performanceImprovement: [],
                riskHistory: []
            }))
            dispatch(resetRiskTab({
                events: []
            }))
        }
    }, [id])



    useEffect(() => {
        if (resultUpdate && id) {
            dispatch(getCaseDetail({ caseId: id, updateCoach: true }));
            dispatch(resetEditCoachPanel({ resultUpdate: null }));
        }
    }, [resultUpdate])

    // Initialize improvement plan states when caseDetail loads
    useEffect(() => {
        if (caseDetail) {
            setNotes(caseDetail.notes || '');
            setImprovementOmit(!!caseDetail.improvementOmit);
            setTargetValues({
                avgEvents: caseDetail.targetAvgEvents || 0,
                riskScore: caseDetail.targetRiskScore || 0,
                totalEvents: caseDetail.targetEvents || 0,
                totalDistance: caseDetail.targetDistance || 0
            });
        }
    }, [caseDetail])

    const resetTabs = () => {
        try {
            setTab(0)
            tabRef?.current?.setValue(0)
        } catch (error) {

        }

    }

    useEffect(() => {
        if (updateDriverPerformanceCaseResult) {
            dispatch(getCaseDetail({ caseId: id, updateCoach: true }))
            dispatch(resetImprovementPlanTab({ updateDriverPerformanceCase: null }))
        }
    }, [updateDriverPerformanceCaseResult])

    const handleChange = () => {
        const preferenceValue = { showDetails: !checked };
        setChecked(!checked);
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: OVERVIEW_KEY, preferenceValue: preferenceValue }));
    };

    // Improvement Plan handlers
    const handleTargetChange = (id, value) => {
        // Validate value before setting state
        let validatedValue = value;

        // Handle null/undefined values
        if (value === null || value === undefined || value === '') {
            validatedValue = null;
        } else {
            // Convert to number if it's a string
            const numValue = typeof value === 'string' ? parseFloat(value) : value;

            // Check if it's a valid number
            if (isNaN(numValue)) {
                validatedValue = null;
            } else {
                // Ensure only positive values
                if (numValue < 0) {
                    validatedValue = 0;
                }
                // Ensure max value is 5000
                else if (numValue > 5000) {
                    validatedValue = 5000;
                } else {
                    validatedValue = numValue;
                }
            }
        }

        setTargetValues(prev => ({
            ...prev,
            [id]: validatedValue
        }));
    };

    const handleNotesChange = (value) => {
        setChangeNotes(true);
        setNotes(value);
    };

    const handleImprovementOmitChange = (value) => {
        setImprovementOmit(value);
    };

    // Tabs
    const tabs = [
        {
            label: messages['driver_performance_case_overview_overview'],
            id: "overview",
            component: <OverviewTab />,
        },
        {
            label: messages['driver_performance_case_overview_risk'],
            id: "risk",
            component: <RiskTab />,
        },
        {
            label: messages['driver_performance_case_overview_improvementPlan'],
            id: "implovementPlan",
            component: <ImplovementPlanTab
                notes={notes}
                changeNotes={changeNotes}
                improvementOmit={improvementOmit}
                targetValues={targetValues}
                onNotesChange={handleNotesChange}
                onImprovementOmitChange={handleImprovementOmitChange}
                onTargetChange={handleTargetChange}
                onNotesCancel={() => {
                    setChangeNotes(false);
                    setNotes(caseDetail?.notes || '');
                }}
                resetTabs={resetTabs}
            />,
        }
    ];

    // Validation for complete button - button should be ENABLED when requirements are met
    const canComplete = () => {
        // Rule 1: Global omit checkbox is checked
        if (improvementOmit) {
            return true;
        }

        // Rule 2: All targets must be filled OR their individual omit checkboxes are checked
        const allTargetsValid = Object.entries(targetValues).every(([key, value]) => {
            // For totalDistance, it doesn't have an omit checkbox, so it must have a value > 0
            if (key === 'totalDistance') {
                return value > 0;
            }

            // For other metrics, either target is filled OR omit checkbox is checked
            const omitChecked = caseDetail?.[`target${key.charAt(0).toUpperCase() + key.slice(1)}Success`] || false;
            return value > 0 || omitChecked;
        });

        return allTargetsValid;
    };

    let nextStep = caseDetail ? caseDetail?.status?.nextStep.replace(/\s+/g, "") : ""

    return (
        <Container className="container-view">
            {(loadingCaseDetail || loadingUpdateDriverPerformanceCase || loadingUpdateCoach || loadingCreateComment || loadingUpdateCaseEventFlags) && <Loading overlay show />}
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: checked ? "15px 20px" : "15px 20px 0 20px" }}
                >
                    <Grid item xs>
                        <Breadcrumb
                            links={[
                                {
                                    title: messages['driver_performance_case_overview_cases'],
                                    link: "/app/driverPerformance/main?tab=cases",
                                },
                                {
                                    title: caseDetail ? caseDetail?.caseNumber : 'N/A',
                                },
                            ]}
                            navigate={navigate}
                        />
                    </Grid>
                    <Grid item xs style={{ display: 'flex', justifyContent: 'end' }}>
                        {tab == 2 ? (
                            <div>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        if (caseDetail?.status?.nextStep == "Judgement") {
                                            let payload = {
                                                id: caseDetail?.id,
                                                statusId: 'completedFailedPI',
                                                updateStatus: true
                                            }
                                            dispatch(updateDriverPerformanceCase(payload));
                                        } else {
                                            // Complete button logic
                                            if (improvementOmit) {
                                                // Case 1: Omit checkbox is checked
                                                let payload = {
                                                    id: caseDetail?.id,
                                                    statusId: 'completedOmitedPI',
                                                    improvementOmit: true,
                                                    updateStatus: true
                                                }
                                                dispatch(updateDriverPerformanceCase(payload));
                                            } else {
                                                // Convert totalDistance from km to miles if isKph is true
                                                let targetDistance = targetValues.totalDistance;

                                                if (isKph && targetDistance > 0) {
                                                    // User entered value in km, convert to miles for DB
                                                    targetDistance = targetDistance / 1.609344;
                                                }

                                                let payload = {
                                                    id: caseDetail?.id,
                                                    statusId: 'evaluate',
                                                    targetAvgEvents: targetValues.avgEvents,
                                                    targetRiskScore: targetValues.riskScore,
                                                    targetEvents: targetValues.totalEvents,
                                                    targetDistance: targetDistance
                                                }

                                                // Case 2: All targets are filled
                                                dispatch(updateDriverPerformanceCase(payload));
                                            }
                                        }
                                    }}
                                    isDisabled={caseDetail?.status?.nextStep == "Judgement" ? false : (!canComplete() || caseDetail?.statusId?.includes('completed') || caseDetail?.statusId?.includes('evaluate'))}
                                    style={{ marginRight: 10 }}
                                >
                                    {
                                        caseDetail?.status?.nextStep == "Judgement" ? messages['close'] : pendingStatus.includes(caseDetail?.statusId) ? messages['driver_performance_case_overview_finish'] : messages['driver_performance_case_overview_finish']
                                    }
                                </Button>
                                {
                                    caseDetail?.status?.nextStep == "Judgement" ? (
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                let payload = {
                                                    id: caseDetail?.id,
                                                    statusId: 'escalateMonitor',
                                                    updateStatus: true
                                                }
                                                dispatch(updateDriverPerformanceCase(payload));
                                            }}
                                        >
                                            {messages['driver_performance_case_overview_escalate']}
                                        </Button>
                                    ) : <></>
                                }
                            </div>
                        ) : <></>}
                        <IconButton
                            icon={checked ? "expand_less" : "expand_more"}
                            tooltip={checked ? messages['driver_performance_case_overview_hideDescription'] : messages['driver_performance_case_overview_showDescription']}
                            onClick={() => handleChange()}
                        />
                    </Grid>
                </Grid>
                {
                    loadingCaseDetail ? (
                        <Skeleton
                            variant="rectangular"
                            animation="wave"
                            height='60px'
                            style={{ marginLeft: 10, marginRight: 10 }}
                        />
                    ) : (
                        <div style={{ marginLeft: 10, marginRight: 10 }}>
                            <Preferences
                                preferencesKey={OVERVIEW_KEY}
                                onInitialPreferences={(data) => {
                                    if (typeof data?.showDetails === "boolean") {
                                        setChecked(data?.showDetails);
                                    }
                                }}
                            >
                                <Collapse in={checked}>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        className="case-details-subheader"
                                    >

                                        {/* Case ID */}
                                        <Grid item>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_caseID']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {caseDetail?.caseNumber ? caseDetail?.caseNumber : 'N/A'}
                                            </span>
                                        </Grid>
                                        {/* Status */}
                                        <Grid item>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_status']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {messages[`driver_performance_case_${caseDetail?.statusId}`] || caseDetail?.statusId}
                                            </span>
                                        </Grid>
                                        {/* Next Step */}
                                        <Grid item>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_nextStep']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {messages[`driver_performance_case_${nextStep}`] || caseDetail?.status?.nextStep || 'N/A'}
                                            </span>
                                        </Grid>
                                        {/* Driver */}
                                        <Grid item style={{ display: 'flex' }}>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_driver']}:
                                            </span>
                                            {
                                                caseDetail?.driver?.id ? (
                                                    <DynamicTooltip
                                                        key={caseDetail?.driver?.id}
                                                        trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{caseDetail?.driver?.name}</span>}
                                                        openOn="click"
                                                        size="medium"
                                                        position="top"
                                                    >
                                                        {(isOpen) => (
                                                            <DriverInfo
                                                                driverKey={caseDetail?.driver?.id}
                                                                openTooltip={isOpen}
                                                                driverId={caseDetail?.driver?.id}
                                                            />
                                                        )}
                                                    </DynamicTooltip>
                                                ) : <span className="case-details-content" style={{ marginRight: 10 }}>
                                                    N/A
                                                </span>
                                            }
                                        </Grid>
                                        {/* Coach */}
                                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_coach']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10, display: 'flex', alignItems: 'center' }}>
                                                {`${caseDetail?.coach?.firstName || ''} ${caseDetail?.coach?.lastName || ''}`.trim() || 'N/A'}
                                            </span>
                                        </Grid>
                                        {/* Created */}
                                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_created']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {caseDetail?.createdOn ? (
                                                    <Timezone
                                                        date={caseDetail?.createdOn}
                                                        dateFormat={dateformat}
                                                        timeFormat="hh:mm a"
                                                        timezone={timeZone}
                                                        className="case-details-date"
                                                    />
                                                ) : 'N/A'}
                                            </span>
                                        </Grid>
                                        {/* Completed */}
                                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_completed']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {caseDetail?.completedDate ? (
                                                    <Timezone
                                                        date={caseDetail?.completedDate}
                                                        dateFormat={dateformat}
                                                        timeFormat="hh:mm a"
                                                        timezone={timeZone}
                                                        className="case-details-date"
                                                    />
                                                ) : 'N/A'}
                                            </span>
                                        </Grid>
                                        {/* Tags */}
                                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className="case-details-title">
                                                {messages['driver_performance_case_overview_tags']}:
                                            </span>
                                            <span className="case-details-content" style={{ marginRight: 10 }}>
                                                {(() => {
                                                    try {
                                                        let tagIds = caseDetail?.driver?.tagIds;
                                                        if (typeof tagIds === 'string') {
                                                            tagIds = JSON.parse(tagIds);
                                                        }
                                                        if (!Array.isArray(tagIds) || tagIds.length === 0) {
                                                            return <span className="case-details-content">N/A</span>;
                                                        }
                                                        const hasAllTags = tagIds.includes("-99") || tagIds.includes(-99);

                                                        if (hasAllTags) {
                                                            return (
                                                                <TagsGroup
                                                                    options={[{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: '#005bf8' }]}
                                                                    nameField="label"
                                                                    colorField="color"
                                                                />
                                                            );
                                                        }
                                                        const availableTags = caseDetail?.driver?.tags?.filter(tag => {
                                                            const isIncluded = tagIds.includes(tag.id) || tagIds.includes(String(tag.id));
                                                            return isIncluded;
                                                        }) || [];

                                                        if (availableTags.length > 0) {
                                                            return (
                                                                <TagsGroup
                                                                    options={availableTags}
                                                                    nameField="label"
                                                                    colorField="color"
                                                                />
                                                            );
                                                        }

                                                        // If there are tagIds but no available tags, create basic tags
                                                        if (tagIds.length > 0 && (!caseDetail?.driver?.tags || caseDetail?.driver?.tags.length === 0)) {
                                                            const basicTags = tagIds.map(id => ({
                                                                id: id,
                                                                label: `Tag ${id}`,
                                                                color: '#005bf8'
                                                            }));
                                                            return (
                                                                <TagsGroup
                                                                    options={basicTags}
                                                                    nameField="label"
                                                                    colorField="color"
                                                                />
                                                            );
                                                        }
                                                        return <span className="case-details-content">N/A</span>;

                                                    } catch (error) {
                                                        return <span className="case-details-content">N/A</span>;
                                                    }
                                                })()}
                                            </span>
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </Preferences>
                        </div>
                    )
                }
                <div className="case-details-tabs">
                    <Tabs
                        ref={tabRef}
                        tab={tab}
                        onChange={(value) => setTab(value)}
                    >
                        {tabs.map((item, index) => {
                            const { label, component } = item;
                            return (
                                <TabContainer key={index} label={label} index={index}>
                                    {component}
                                </TabContainer>
                            );
                        })}
                    </Tabs>
                </div>
            </Paper>
        </Container>
    )
}

export default CustomerDetail
